import {
  endsWith,
  every,
  includes,
  isArray,
  isObject,
  isString,
  keys,
  map,
  mapValues,
  size,
  toLower,
  trim,
} from "lodash";
import { isProduction } from "utils/determineSite";
import { states } from "statics/countriesAndStates";
import { PhoneNumberUtil } from "google-libphonenumber";
import moment from "moment";

import * as yup from "yup";

const domainRegex = /^(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
const pathRegex = /^\/?[\w\-.+~!$'()*,;=:@]*$/;

export const PRINTABLE_ASCII_MESSAGE =
  "${path} can only contain letters, numbers, special characters, or spaces.";
export const NAME_MESSAGE =
  "${path} may only contain letters, apostrophes, hyphens, slashes, or spaces.";
export const NAME_LENGTH_ERROR =
  "Combined first and last name must be less than 30 characters.";
export const ALPHANUMERIC_SPACES_MESSAGE =
  "${path} can only contain letters, numbers, spaces, #, /, and -.";
export const INVALID_SSN_ERROR = "${path} is invalid.";
export const INVALID_PHONE_NUMBER = "Please enter a valid phone number";
export const DOB_FORMAT = "MM/DD/YYYY";
export const DOB_FORMAT_ERROR =
  "Please enter your date of birth as MM/DD/YYYY, including a 4-digit year.";
export const PO_BOX_ERROR =
  "${path} cannot be a P.O. Box, please use a residential or business address.";

export const whiteSpaceError = "value contains trailing or leading whitespace";

export const noWhitespaceRegex = /^$|^[^\s]+(\s+[^\s]+)*$/;
const poBoxRegex =
  /^(((p[\s.]?[o\s][.]?)\s?)|(post\s?office\s?))((box|bin|b\.?)?\s?(num|number|#)?\s?\d+)/gim;

const einRegEx = [/^\d{2}-?\d{7}$/];

export const usernameRegex = /^([a-zA-Z0-9+\-_.!#$'^`~@]{2,25})$/;

export function testEIN(value: string | undefined) {
  for (let i = 0; i < einRegEx.length; i++) {
    if (einRegEx[i].test(value as string)) {
      return true;
    }
  }
  return false;
}

export function isPoBox(address: string | undefined) {
  const uspsValidPoBoxRegex = /po\sbox|p.o.\sbox/i;
  return uspsValidPoBoxRegex.test(address as string);
}

export function testPoBoxAddress(address: string | undefined) {
  return !poBoxRegex.test(address as string);
}

export function testPrintableAsciiCharacters(value: string | undefined) {
  // matches characters from range "SPACE" to ~ (Char range 32-126)
  const addressCharRegex = /^([ -~]*)$/;
  return addressCharRegex.test(value as string);
}

export function testAlphanumericSpaces(value: string | undefined) {
  const alphanumericSpacesRegex = /^[a-zA-Z0-9 #/-]*$/;
  return alphanumericSpacesRegex.test(value as string);
}

export function booleanValue(val: unknown) {
  if (val) {
    return (
      val === true ||
      // eslint-disable-next-line @typescript-eslint/no-base-to-string -- accepts "true"/"TRUE" string variants; non-string objects stringify to "[object Object]" and correctly fail the check
      String(val).toLowerCase() === "true"
    );
  }
  return false;
}

interface AddressFields {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export function validateUsersAddress(
  userAddress: AddressFields,
  validatedAddress: AddressFields
) {
  const propertiesToCheck = [
    "address1",
    "address2",
    "city",
    "state",
    "postalCode",
  ];

  // if user has put in a po box want to make sure we don't consider valid
  if (
    isPoBox(validatedAddress.address1) ||
    isPoBox(validatedAddress.address2)
  ) {
    return false;
  }

  // if what user passed in matches USPS minus casing return true
  return every(
    propertiesToCheck,
    (property) =>
      userAddress[property as keyof AddressFields] ==
      validatedAddress[property as keyof AddressFields]
  );
}

export function testUsername(
  this: { createError: (opts: { message: string }) => unknown },
  username: string | undefined
) {
  // Deliberately yup's loose built-in `.email()`, not our strict `testEmail` —
  // this is "does the username look email-ish at all?", so the wider net is the
  // correct behaviour here. A username of "john@gmail" should still be refused.
  const localSchema = yup.string().email();
  const isEmail = localSchema.isValidSync(username);

  if (isEmail) {
    return this.createError({
      message: "Username cannot be an email address.",
    });
  }

  if (!usernameRegex.test(username)) {
    return this.createError({
      message:
        "Username can only contain alphanumeric characters and the following characters: '_', '+', '-', '.', '!', '#', '$', ''', '^', '`', '~' and '@'.",
    });
  }

  return true;
}

export function testName(name: string | undefined) {
  const nameRegex = /^([a-zA-Z'\-/\s]*)$/;
  return nameRegex.test(name as string);
}

export function testFirstLastName(this: {
  parent: { firstName?: string; lastName?: string };
}) {
  const firstName = this.parent.firstName;
  const lastName = this.parent.lastName;
  return size(firstName) + size(lastName) < 30;
}

export const alphanumericSpacesValidator = yup
  .string()
  .trim()
  .test(
    "alphanumeric-spaces",
    ALPHANUMERIC_SPACES_MESSAGE,
    testAlphanumericSpaces
  )
  .matches(noWhitespaceRegex, whiteSpaceError);

export const STATE_MESSAGE =
  "${path} must be a two-letter state code, e.g. NY.";

export const stateAbbreviations = keys(states);

const lowerStateAbbreviations = map(stateAbbreviations, toLower);

// Casing is not part of the contract — "ny" and "NY" both pass.
export function testStateAbbreviation(value: unknown) {
  if (value === undefined || value === null || value === "") return true;
  return isString(value) && includes(lowerStateAbbreviations, toLower(value));
}

export const stateValidator = yup
  .string()
  .label("State")
  .required()
  .test("state", STATE_MESSAGE, testStateAbbreviation);

export const postalCodeValidator = yup
  .string()
  .trim()
  .label("Zip")
  .required()
  .test("postal-code", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
  .max(5)
  .min(5)
  .matches(noWhitespaceRegex, whiteSpaceError);

// Strict MM/DD/YYYY with a required 4-digit year — e.g. "12/12/94" is rejected,
// "12/12/1994" is accepted. Guards against partial mask values reaching the API.
export const dobFormatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;

// Parse a date-of-birth string strictly as MM/DD/YYYY. Use for any age/order math
// so a 2-digit year or a non-existent calendar date never coerces to a Date.
export const parseDob = (value: string | undefined) =>
  moment(value, DOB_FORMAT, true);

// Reusable Yup test: the value must be a real MM/DD/YYYY date with a 4-digit year.
// Apply to every date-of-birth field; keep each form's own age/match rules separate.
export const testDobFormat = (value: string | undefined) =>
  dobFormatRegex.test(value as string) && parseDob(value).isValid();

// The roster/platform API expects DOB on the wire as YYYY-MM-DD, while our forms
// collect it as MM/DD/YYYY. Normalize a single MM/DD/YYYY value to the wire format;
// pass through anything else unchanged (already-wire values, or malformed input the
// backend validator should reject) so this never silently mangles a date.
export const DOB_WIRE_FORMAT = "YYYY-MM-DD";
export const toWireDob = (value: string | undefined) =>
  testDobFormat(value) ? parseDob(value).format(DOB_WIRE_FORMAT) : value;

// Normalize the `dob` on every row of a roster payload to the wire format. Centralized
// here so all `uploadRoster` callers (single-add, CSV bulk) ship a consistent format.
export const normalizeRosterDobs = <T>(rows: T): T =>
  isArray(rows)
    ? (map(rows, (row: { dob?: string }) =>
        isObject(row) && row.dob ? { ...row, dob: toWireDob(row.dob) } : row
      ) as T)
    : rows;

// Standard individual DOB: format + must be 18–100. Other forms reuse `testDobFormat`
// with their own age rules (e.g. beneficiaries may be minors).
export const dobValidator = yup
  .string()
  .label("Date Of Birth")
  .required()
  .test("dob-format", DOB_FORMAT_ERROR, testDobFormat)
  .test(
    "dob-min-age",
    "You must be at least 18 to create an account.",
    (value) =>
      parseDob(value).isValid() && moment().diff(parseDob(value), "years") >= 18
  )
  .test(
    "dob-max-age",
    "You must be under 100 years old to create an account.",
    (value) =>
      parseDob(value).isValid() && moment().diff(parseDob(value), "years") < 100
  );

// --- Email -----------------------------------------------------------------
// Mirrors icon-backend's `emailValidator`/`testEmail` in src/validators/helpers.js
// — the two regexes must stay identical, or a form that passes here fails on
// submit with the backend's own error.
//
// Do NOT use yup's built-in `.email()` for email fields: its regex changes
// between yup versions. yup 0.32 required a dotted domain; yup 1.x switched to
// the HTML5 `<input type=email>` regex, which accepts TLD-less addresses
// ("john@gmail"), single-character and numeric TLDs ("a@b", "a@b.c1"), while
// newly rejecting quoted and non-ASCII local parts. Validating against our own
// regex keeps both repos in agreement and survives the next yup upgrade.
export const EMAIL_MESSAGE = "You must enter a valid email address.";

// RFC 5322 dot-atom local part (unquoted — no real signup uses the quoted form),
// then one or more DNS labels followed by an alphabetic TLD of two or more
// characters. That trailing `\.TLD` requirement is what rejects "john@gmail".
const emailLocalPart =
  "[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*";
const emailDomainLabel = "[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?";
const emailTld = "[A-Za-z]{2,63}";
export const emailRegex = new RegExp(
  `^${emailLocalPart}@(?:${emailDomainLabel}\\.)+${emailTld}$`
);

export const EMAIL_MAX_LENGTH = 254; // RFC 5321 forward-path limit
const EMAIL_LOCAL_MAX_LENGTH = 64; // RFC 5321 local-part limit

// Reusable Yup test. Empty values pass so `.required()`/`.nullable()` keep owning
// presence — the same division of labour yup's own `.email()` had.
export function testEmail(value: unknown) {
  if (value === undefined || value === null || value === "") return true;
  if (typeof value !== "string") return false;
  if (value.length > EMAIL_MAX_LENGTH) return false;

  const [localPart] = value.split("@");
  if (localPart.length > EMAIL_LOCAL_MAX_LENGTH) return false;

  return emailRegex.test(value);
}

// Drop-in replacement for `yup.string().email(message)`. A factory rather than a
// shared schema so each field keeps its own message and its own `.label()`,
// `.required()`, `.nullable()` and `.max()` chain.
export const emailValidator = (message: string = EMAIL_MESSAGE) =>
  yup.string().test("email", message, testEmail);

export const formatErrorString = (errMsg: string) => {
  const message = endsWith(errMsg, ".") ? errMsg : errMsg + ".";
  return message;
};

export const urlValidator = (url: string | null | undefined) => {
  if (!url) return true;
  const [domain, path] = url
    .replace("http://", "")
    .replace("https://", "")
    .split("/");

  const domainIsValid = domainRegex.test(domain);
  const pathIsValid = pathRegex.test(path);
  return domainIsValid && pathIsValid;
};

export const IndividualAddress1Validator = yup
  .string()
  .label("Address 1")
  .required()
  .matches(noWhitespaceRegex, whiteSpaceError)
  .min(3, "Must be at least three characters.")
  .max(40)
  .test("po-box-address", PO_BOX_ERROR, testPoBoxAddress)
  .test("address1", ALPHANUMERIC_SPACES_MESSAGE, testAlphanumericSpaces)
  .test(
    "address1",
    "${path} can only contain letters, numbers, spaces, and #.",
    testPrintableAsciiCharacters
  );

export const address2Validator = yup
  .string()
  .label("Address 2")
  .test("address2", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
  .test(
    "address2",
    "${path} can only contain letters, numbers, spaces, and #.",
    testAlphanumericSpaces
  )
  .trim()
  .matches(noWhitespaceRegex, whiteSpaceError)
  .max(30)
  .min(3, "Must be at least three characters.")
  .transform((value: string | undefined) => (value ? value : null))
  .nullable();

export const individualAddress2Validator = yup
  .string()
  .label("Address 2")
  .test("address2", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
  .test(
    "address2",
    "${path} can only contain letters, numbers, spaces, and #.",
    testAlphanumericSpaces
  )
  .test("po-box-address", PO_BOX_ERROR, testPoBoxAddress)
  .trim()
  .matches(noWhitespaceRegex, whiteSpaceError)
  .max(30)
  .min(3, "Must be at least three characters.")
  .transform((value: string | undefined) => (value ? value : null))
  .nullable();

export const EmployerAddress1Validator = yup
  .string()
  .label("Address 1")
  .required()
  .matches(noWhitespaceRegex, whiteSpaceError)
  .min(3, "Must be at least three characters.")
  .max(40)
  .test("po-box-address", PO_BOX_ERROR, testPoBoxAddress)
  .test("address1", ALPHANUMERIC_SPACES_MESSAGE, testAlphanumericSpaces)
  .test(
    "address1",
    "${path} can only contain letters, numbers, spaces, and #.",
    testPrintableAsciiCharacters
  );

export const EmployerAddress2Validator = yup
  .string()
  .label("Address 2")
  .test("address2", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
  .test(
    "address2",
    "${path} can only contain letters, numbers, spaces, and #.",
    testAlphanumericSpaces
  )
  .test("po-box-address", PO_BOX_ERROR, testPoBoxAddress)
  .trim()
  .matches(noWhitespaceRegex, whiteSpaceError)
  .max(30)
  .min(3, "Must be at least three characters.")
  .transform((value: string | undefined) => (value ? value : null))
  .nullable();

export const companyInviteCodeRegex = /^[a-zA-Z0-9]{7}-\d{1,2}$/;
export const uniqueInviteCodeRegex = /^[a-zA-Z0-9]{7}$/;
// The two regexes above are unused, but help to illustrate the difference between the two in the regex below.
export const validIconInvite = /^[a-zA-Z0-9]{7}-\d{1,2}$|^[a-zA-Z0-9]{7}$/;
// Takes a regex that allows a-z, A-Z, 0-9, -, and spaces then negates it.
export const unallowedInvitationCodeCharacters = /[^a-zA-Z0-9-]*/g;
export const invitationCodeParser = (event: { target: { value: string } }) => {
  const newValue = event.target.value;
  return trim(newValue).replace(unallowedInvitationCodeCharacters, "");
};
export const invitationCodeError =
  "Invalid invitation code, please make sure there are no leading or trailing special characters.";

export const whiteSpaceFieldTrimmer = (value: unknown): unknown => {
  if (isString(value)) {
    return value.trim();
  } else if (isArray(value)) {
    return map(value, whiteSpaceFieldTrimmer);
  } else if (isObject(value)) {
    return mapValues(value, whiteSpaceFieldTrimmer);
  }
  return value;
};

export function testPhoneNumber(value: string | null | undefined) {
  if (!value) return false;

  try {
    const cleanedInput = value.replace(/[^\d+]/g, "");

    const phoneUtil = PhoneNumberUtil.getInstance();
    const phoneNumber = cleanedInput.startsWith("+")
      ? phoneUtil.parse(cleanedInput)
      : phoneUtil.parse(cleanedInput, "US");

    return phoneUtil.isValidNumber(phoneNumber);
  } catch {
    return false;
  }
}

export function testSsn(value: string | undefined, allowNoDash?: boolean) {
  const isInProduction = isProduction();

  if (!isInProduction) {
    const matchesWithDashes = /^\d{3}-\d{2}-\d{4}$/.test(value as string);
    if (matchesWithDashes) return true;
    if (allowNoDash) {
      return /\d{9}/.test(value as string);
    }
    return false;
  } else {
    if (!value) return false;

    const cleanedValue = value.replace(/-/g, "");

    const withDashesFormat = /^\d{3}-\d{2}-\d{4}$/.test(value);
    const withoutDashesFormat = allowNoDash && /^\d{9}$/.test(cleanedValue);

    if (!withDashesFormat && !withoutDashesFormat) {
      return false;
    }

    const parts = cleanedValue.match(/^(\d{3})(\d{2})(\d{4})$/);
    if (!parts) return false;

    const [, area, group, serial] = parts;

    // Check rules for SSN validation
    // 1. No all-zeros groups
    if (area === "000" || group === "00" || serial === "0000") {
      return false;
    }

    // 2. No 666 in area and no 900-999 series
    if (area === "666" || parseInt(area) >= 900) {
      return false;
    }

    // 3. Check for blacklisted numbers
    const blacklist = [
      "078051120", // Woolworth wallet SSN
      "219099999", // Used for advertising
      "123456789", // Obviously invalid sequential number
      "111111111", // Repeating digits
      "222222222",
      "333333333",
      "444444444",
      "555555555",
      "666666666",
      "777777777",
      "888888888",
      "999999999",
      "987654320", // Wallet manufacturers
      "987654321", // Wallet manufacturers
      "987654322", // Wallet manufacturers
      "987654323", // Wallet manufacturers
      "987654324", // Wallet manufacturers
      "987654325", // Wallet manufacturers
      "987654326", // Wallet manufacturers
      "987654327", // Wallet manufacturers
      "987654328", // Wallet manufacturers
      "987654329", // Wallet manufacturers
    ];

    if (blacklist.includes(cleanedValue)) {
      return false;
    }

    return true;
  }
}
